import React, { useMemo, useCallback } from 'react';
import { ExtendedWebsiteMessage } from '../../types/messages';
import { MessageDisplayHelpers } from '../../types/messages';
import './MessageListItem.css';

interface MessageListItemProps {
  message: ExtendedWebsiteMessage;
  selected: boolean;
  isActive: boolean;
  onSelect: (selected: boolean) => void;
  onClick: () => void;
}

/**
 * Email-style compact message list item
 * Shows: sender/source, title, preview, timestamp, unread indicator
 */
const MessageListItem: React.FC<MessageListItemProps> = ({
  message,
  selected,
  isActive,
  onSelect,
  onClick
}) => {
  // A message is considered "read" if it has been viewed OR dismissed
  // This ensures dismissed messages (e.g., survey completed, team applications) appear as read
  const isUnread = !message.viewedAt && !message.isDismissed;

  // Get message source display with Bootstrap Icons
  const sourceDisplay = useMemo(() => {
    const display = MessageDisplayHelpers.getMessageSourceDisplay(message.messageSource, message.projectTitle);
    // Use Bootstrap Icons for better cross-platform compatibility
    const iconMap: Record<string, string> = {
      'global': 'bi-globe',
      'project_release': 'bi-rocket-takeoff',
      'task_related': 'bi-check-circle',
      'survey_related': 'bi-bar-chart'
    };
    return {
      ...display,
      iconClass: iconMap[message.messageSource] || 'bi-envelope'
    };
  }, [message.messageSource, message.projectTitle]);

  // Get message type display with Bootstrap Icons
  const typeDisplay = useMemo(() => {
    const display = MessageDisplayHelpers.getMessageTypeDisplay(message.message_type);
    // Map emoji icons to Bootstrap Icons for better rendering
    const iconClassMap: Record<string, string> = {
      'ℹ️': 'bi-info-circle',
      '👋': 'bi-hand-wave',
      '📢': 'bi-megaphone',
      '🔧': 'bi-wrench',
      '⏳': 'bi-hourglass-split',
      '✅': 'bi-check-circle-fill',
      '❌': 'bi-x-circle-fill',
      '🎉': 'bi-party-popper',
      '📝': 'bi-file-text'
    };
    return {
      ...display,
      iconClass: iconClassMap[display.icon] || 'bi-file-text'
    };
  }, [message.message_type]);

  // Get preview text (first 100 chars of message content)
  const previewText = useMemo(() => {
    const cleanContent = MessageDisplayHelpers.removeActionButtonMarkup(message.message_text);
    const plainText = cleanContent.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    return plainText.length > 100 ? plainText.substring(0, 100) + '...' : plainText;
  }, [message.message_text]);

  // Handle checkbox change
  const handleCheckboxChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    onSelect(event.target.checked);
  }, [onSelect]);

  // Handle item click
  const handleClick = useCallback(() => {
    onClick();
  }, [onClick]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  }, [onClick]);

  return (
    <div
      className={`message-list-item ${isUnread ? 'unread' : 'read'} ${isActive ? 'active' : ''} ${selected ? 'selected' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Message from ${sourceDisplay.label}: ${typeDisplay.label}`}
    >
      {/* Checkbox for selection */}
      <div className="message-list-item-checkbox">
        <input
          type="checkbox"
          checked={selected}
          onChange={handleCheckboxChange}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select message from ${sourceDisplay.label}`}
        />
      </div>

      {/* Unread indicator */}
      {isUnread && (
        <div className="message-list-item-unread-indicator" aria-label="Unread message">
          <span className="unread-dot"></span>
        </div>
      )}

      {/* Bookmark indicator */}
      {message.isBookmarked && (
        <div className="message-list-item-bookmark" aria-label="Bookmarked">
          ⭐
        </div>
      )}

      {/* Message content - Two-line layout */}
      <div className="message-list-item-content">
        {/* Line 1: Subject/Title with Time on the right */}
        <div className="message-list-item-text">
          <div className="message-list-item-subject">
            <div className="message-list-item-subject-content">
              <span className={`type-icon type-${message.message_type}`}>
                <i className={`bi ${typeDisplay.iconClass}`} aria-hidden="true"></i>
              </span>
              <span className="subject-text">{typeDisplay.label}</span>
            </div>
            <time className="message-list-item-time" dateTime={message.created_at}>
              {message.timeAgo}
            </time>
          </div>
          
          {/* Line 2: Preview text */}
          <div className="message-list-item-preview">
            {previewText}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(MessageListItem);
